/**
 * FullLayout Component
 * Main application layout with header, sidebar, and content area
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import TouchAppIcon from '@mui/icons-material/TouchApp';


import Header from './Header';
import Sidebar from './Sidebar';
import AIAssistant from '../modules/workflow/components/AIAssistant';

const AI_LAUNCHER_STORAGE_KEY = 'mdm.aiAssistantLauncherPositionPercent';
const AI_LAUNCHER_SIZE = 56;
const AI_LAUNCHER_MARGIN = 24;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getDefaultLauncherPosition = () => {
  if (typeof window === 'undefined') {
    return { x: 95, y: 95 };
  }
  const xPx = window.innerWidth - AI_LAUNCHER_SIZE - AI_LAUNCHER_MARGIN;
  const yPx = window.innerHeight - AI_LAUNCHER_SIZE - AI_LAUNCHER_MARGIN;
  return {
    x: (xPx / window.innerWidth) * 100,
    y: (yPx / window.innerHeight) * 100,
  };
};

const readLauncherPosition = () => {
  if (typeof window === 'undefined') {
    return getDefaultLauncherPosition();
  }

  try {
    const raw = window.localStorage.getItem(AI_LAUNCHER_STORAGE_KEY);
    if (!raw) {
      return getDefaultLauncherPosition();
    }

    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return parsed;
    }
  } catch {
    // Ignore storage errors and fall back to the default position.
  }

  return getDefaultLauncherPosition();
};

/**
 * FullLayout
 * Complete authenticated layout with header, sidebar, and main content
 * 
 * @returns {React.ReactNode}
 */
const FullLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const aiLayerZIndex = theme.zIndex.modal - 1;
  const launcherRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    didDrag: false,
    pointerId: null,
    offsetX: 0,
    offsetY: 0,
    startClientX: 0,
    startClientY: 0,
  });

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [launcherPosition, setLauncherPosition] = useState(() => readLauncherPosition());

  const handleToggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleCloseSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Close AI Assistant and reset launcher position whenever the route/location changes
  React.useEffect(() => {
    setAiAssistantOpen(false);
    setLauncherPosition(getDefaultLauncherPosition());
    window.localStorage.removeItem(AI_LAUNCHER_STORAGE_KEY);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const clampToViewport = () => {
      setLauncherPosition((prev) => {
        const xPx = (prev.x / 100) * window.innerWidth;
        const yPx = (prev.y / 100) * window.innerHeight;

        const maxX = Math.max(window.innerWidth - AI_LAUNCHER_SIZE - 8, 0);
        const maxY = Math.max(window.innerHeight - AI_LAUNCHER_SIZE - 8, 0);
        
        const clampedXPx = clamp(xPx, 8, maxX);
        const clampedYPx = clamp(yPx, 8, maxY);

        const next = {
          x: (clampedXPx / window.innerWidth) * 100,
          y: (clampedYPx / window.innerHeight) * 100,
        };

        window.localStorage.setItem(AI_LAUNCHER_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    };

    clampToViewport();
    window.addEventListener('resize', clampToViewport);

    return () => {
      window.removeEventListener('resize', clampToViewport);
    };
  }, []);

  const handleLauncherPointerDown = (event) => {
    if (aiAssistantOpen) {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    const target = launcherRef.current;
    if (!target) {
      return;
    }

    const rect = target.getBoundingClientRect();
    dragStateRef.current = {
      isDragging: false,
      didDrag: false,
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      startClientX: event.clientX,
      startClientY: event.clientY,
    };

    target.setPointerCapture(event.pointerId);
  };

  const handleLauncherPointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (dragState.pointerId !== event.pointerId) {
      return;
    }

    const target = launcherRef.current;
    if (!target) {
      return;
    }

    const distance = Math.hypot(
      event.clientX - dragState.startClientX,
      event.clientY - dragState.startClientY
    );
    if (distance > 4) {
      dragState.isDragging = true;
      dragState.didDrag = true;
    }

    if (!dragState.isDragging) {
      return;
    }

    const maxX = Math.max(window.innerWidth - AI_LAUNCHER_SIZE - 8, 8);
    const maxY = Math.max(window.innerHeight - AI_LAUNCHER_SIZE - 8, 8);
    const xPx = clamp(event.clientX - dragState.offsetX, 8, maxX);
    const yPx = clamp(event.clientY - dragState.offsetY, 8, maxY);
    const next = {
      x: (xPx / window.innerWidth) * 100,
      y: (yPx / window.innerHeight) * 100,
    };

    setLauncherPosition(next);
  };

  const handleLauncherPointerUp = (event) => {
    const dragState = dragStateRef.current;
    if (dragState.pointerId !== event.pointerId) {
      return;
    }

    const target = launcherRef.current;
    if (target?.hasPointerCapture?.(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }

    window.localStorage.setItem(AI_LAUNCHER_STORAGE_KEY, JSON.stringify(launcherPosition));

    const wasDragging = dragState.didDrag;
    dragStateRef.current = {
      isDragging: false,
      didDrag: false,
      pointerId: null,
      offsetX: 0,
      offsetY: 0,
    };

    if (!wasDragging) {
      setAiAssistantOpen(true);
    }
  };

  // const showAIAssistant = location.pathname.includes('/hcp-merge') || location.pathname.includes('/hcp-dcr');
  const batchId = searchParams.get('batchId');
  const showSidebar =
  !location.pathname.startsWith('/feedback') &&
  !location.pathname.startsWith('/prompt-rules');
  
  const showAIAssistant = showSidebar;
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', overflowX: 'clip' }}>
      {/* Header */}
      <Header onToggleSidebar={handleToggleSidebar} />

      {/* Sidebar */}
      {showSidebar && (
        <Sidebar open={sidebarOpen} onClose={handleCloseSidebar} />
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          mt: '72px',
          mr:1,
          ml: showSidebar ? 0 : 1,
          mb:1,
          height: 'calc(100vh - 80px)',
          borderRadius: '12px',
          borderTop: '1px solid #e2e8f0',
          border: '1px solid #e2e8f0',
          bgcolor: '#fff',
          overflow: 'hidden',
          pr: showAIAssistant && aiAssistantOpen ? { lg: '360px', xl: '392px' } : 0,
          flex: 1,
        }}
      >
        <Box
        className="p-3 pb-0"
          sx={{
                //  ...theme.typography.mainContent,
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor:"#fafbfc"
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* AI Assistant Sidebar Panel */}
      {showAIAssistant && aiAssistantOpen && (
        <Box
          sx={{
            mt:1,
            mx:0.7,
            position: 'fixed',
            top: { xs: 0, lg: 85 }, // Account for header height on larger screens
            right: { xs: 0, lg: 12, xl: 14 },
            bottom: { xs: 0, lg: 14 },
            width: { xs: '100%', lg: 344, xl: 372 },
            zIndex: aiLayerZIndex,
            border: '1px solid #ffffff',
            borderColor: "#e2e8f0",
            bgcolor: '#ffffff',
            borderRadius: { xs: 0, lg: '18px' },
            boxShadow: '0 8px 28px rgba(237, 237, 237, 0.06)',
            overflow: 'hidden',
          }}
        >
          <AIAssistant onClose={() => setAiAssistantOpen(false)} batchId={batchId} />
        </Box>
      )}

      {/* Floating Action Button for AI Assistant */}
      {showAIAssistant && !aiAssistantOpen && (
        <Tooltip title="Ask AI Assistant" placement="left">
          <Box
            ref={launcherRef}
            onPointerDown={handleLauncherPointerDown}
            onPointerMove={handleLauncherPointerMove}
            onPointerUp={handleLauncherPointerUp}
            sx={{
              position: 'fixed',
              left: `${launcherPosition.x}%`,
              top: `${launcherPosition.y}%`,
              zIndex: aiLayerZIndex,
              width: AI_LAUNCHER_SIZE,
              height: AI_LAUNCHER_SIZE,
              borderRadius: '50%',
              backgroundColor: '#1d8ef0',
              boxShadow: '0 4px 16px rgba(29,142,240,0.4)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
              touchAction: 'none',

              transition: 'transform 0.2s ease-in-out, background-color 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'scale(1.08)',
                backgroundColor: '#177cd2',
                boxShadow: '0 8px 20px rgba(29,142,240,0.45)',
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            <i className="fa-solid fa-robot" style={{ color: 'white', fontSize: '28px' }}></i>

          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default FullLayout;
 